// useAkteStand.ts — Bearbeitungsstand und bekannte Kindzeilen der Akte
// (28.09.2026, utils/kontaktKonflikt.ts).
//
// `merken(kontakt)` nach jedem Laden; `rumpf(body)` ergänzt den Diff-PUT um
// `erwarteterStand` und `bekannteZeilen`. Eigene Kopf-Speicherungen (siehe
// utils/kontaktStand.ts) ziehen den Stand mit, fremde nicht.

import * as React from "react";
import { bekannteZeilenVon, rumpfMitStand, standVon } from "../../utils/kontaktKonflikt";
import { aufEigenenStandHoeren, standNachEigenemSchreiben } from "../../utils/kontaktStand";

export function useAkteStand(contactId: number): {
  merken: (kontakt: Record<string, unknown>) => void;
  /** Nach dem eigenen erfolgreichen PUT: Stand aus der Antwort. */
  gespeichert: (antwort: unknown) => void;
  rumpf: (body: Record<string, unknown>) => Record<string, unknown>;
} {
  const standRef = React.useRef<number | null>(null);
  const bekanntRef = React.useRef<Record<string, number[]> | null>(null);

  React.useEffect(
    () =>
      aufEigenenStandHoeren(contactId, (neu) => {
        standRef.current = standNachEigenemSchreiben(standRef.current, neu);
      }),
    [contactId],
  );

  const merken = React.useCallback((kontakt: Record<string, unknown>) => {
    standRef.current = standVon(kontakt);
    bekanntRef.current = bekannteZeilenVon(kontakt);
  }, []);

  const gespeichert = React.useCallback((antwort: unknown) => {
    const a = antwort as Record<string, unknown> | null;
    if (!a) return;
    const v = standVon(a);
    if (v != null) standRef.current = v;
    // Nach dem Speichern kennt der Client auch die eben angelegten Zeilen.
    // Nur, wenn die Antwort die Listen trägt — sonst gälte „kannte keine".
    if (Array.isArray(a.communications)) bekanntRef.current = bekannteZeilenVon(a);
  }, []);

  const rumpf = React.useCallback(
    (body: Record<string, unknown>) => rumpfMitStand(body, standRef.current, bekanntRef.current),
    [],
  );

  return { merken, gespeichert, rumpf };
}
